import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import {
  Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, Skeleton, Snackbar, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import moment from 'moment';
import PhoneIcon from '@mui/icons-material/Phone';
import AddLinkIcon from '@mui/icons-material/AddLink';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { vt, cardSx } from '../../../styles/voip-theme';
import voipService from '../../../services/voip.service';
import VoipCallDetailsModal from '../../../components/voip/voip-call-details-modal';

// =====================================================================
// VoipCallsHistory — card de histórico de chamadas do tenant (dados REAIS).
// Embutido na tela /voip-platforms (não é mais um menu separado).
// =====================================================================

const fmtClock = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
};
const fmtWhen = (iso) => (iso ? moment(iso).format('DD/MM/YYYY HH:mm') : '—');
const callNumber = (c) => (c.direction === 'inbound' ? c.fromNumber : c.toNumber) || '—';

function VoipCallsHistory(props) {
  const { intl, history, lang } = props;
  const f = (id, def) => intl.formatMessage({ id, defaultMessage: def });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('all');
  const [status, setStatus] = useState('all');
  const [playingId, setPlayingId] = useState(null);
  // associação manual de chamada órfã
  const [assocCallId, setAssocCallId] = useState(null);
  const [assocInput, setAssocInput] = useState('');
  const [associating, setAssociating] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [assocError, setAssocError] = useState('');
  const [snack, setSnack] = useState('');
  const [detailCall, setDetailCall] = useState(null);   // modal "mais informações"
  const mountedRef = useRef(true);

  const openAssociate = (callId) => { setAssocCallId(callId); setAssocInput(''); setAssocError(''); };
  const closeAssociate = () => { if (!associating) setAssocCallId(null); };
  const submitAssociate = () => {
    const calledId = parseInt(String(assocInput).replace(/\D/g, ''), 10);
    if (!calledId) { setAssocError(f('voip.calls.assoc.invalid', 'Informe um número de chamado válido.')); return; }
    setAssociating(true); setAssocError('');
    voipService.associateCalled(assocCallId, calledId)
      .then((res) => {
        if (!mountedRef.current) return;
        if (res && res.success) {
          setRows((prev) => prev.map((r) => (r.id === assocCallId ? { ...r, calledId } : r)));
          setSnack(f('voip.calls.assoc.ok', 'Chamada associada ao chamado') + ` #${calledId}`);
          setAssocCallId(null);
        } else {
          setAssocError((res && res.errors && res.errors[0]) || f('voip.calls.assoc.notfound', 'Chamado não encontrado.'));
        }
      })
      .catch(() => { if (mountedRef.current) setAssocError(f('voip.calls.assoc.fail', 'Falha ao associar o chamado.')); })
      .then(() => { if (mountedRef.current) setAssociating(false); });
  };

  // Força a conciliação no backend (repuxa o history dos provedores poll) e recarrega a lista.
  const forceReconcile = () => {
    setReconciling(true);
    voipService.reconcileNow()
      .then((res) => {
        if (res && res.success) { setSnack(f('voip.calls.reconcile.ok', 'Conciliação disparada — atualizando…')); load(); }
        else setSnack(f('voip.calls.reconcile.err', 'Não foi possível conciliar agora.'));
      })
      .catch((err) => {
        const st = err && err.response && err.response.status;
        setSnack(st === 429 ? f('voip.calls.reconcile.limit', 'Aguarde alguns segundos antes de forçar de novo.') : f('voip.calls.reconcile.err', 'Não foi possível conciliar agora.'));
      })
      .then(() => { if (mountedRef.current) setReconciling(false); });
  };

  const load = () => {
    setLoading(true);
    voipService.getRecentCalls(50)
      .then((res) => { if (mountedRef.current) setRows(((res && res.data) || []).map(voipService.normalizeCall)); })
      .catch(() => {})
      .then(() => { if (mountedRef.current) setLoading(false); });
  };

  useEffect(() => { mountedRef.current = true; load(); return () => { mountedRef.current = false; }; }, []);

  // Atualiza a lista quando algum fluxo VOIP sinaliza (ex.: "Concluir" no widget, associação, reconcile).
  useEffect(() => {
    const h = () => load();
    window.addEventListener('voip:refresh-calls', h);
    return () => window.removeEventListener('voip:refresh-calls', h);
  }, []);


  const statusChip = (st) => {
    const map = {
      initiated: { bg: vt.blueSoft, fg: vt.blue, label: f('voip.call.status.initiated', 'Discada') },
      ringing: { bg: vt.blueSoft, fg: vt.blue, label: f('voip.call.status.ringing', 'Chamando') },
      answered: { bg: vt.greenGrad, fg: '#fff', label: f('voip.call.status.answered', 'Atendida') },
      ended: { bg: vt.greenGrad, fg: '#fff', label: f('voip.call.status.answered', 'Atendida') },
      missed: { bg: '#FEF2F2', fg: '#B91C1C', label: f('voip.call.status.missed', 'Não atendida') },
      failed: { bg: '#F1F5F9', fg: '#475569', label: f('voip.call.status.failed', 'Falhou') },
    };
    const c = map[st] || { bg: '#F1F5F9', fg: '#475569', label: st || '—' };
    return <Box component="span" sx={{ display: 'inline-block', px: 1, py: '3px', borderRadius: 999, background: c.bg, color: c.fg, fontWeight: 700, fontSize: 11 }}>{c.label}</Box>;
  };

  const filtered = rows.filter((c) => {
    if (direction !== 'all' && c.direction !== direction) return false;
    if (status !== 'all' && c.status !== status) return false;
    const q = search.trim().toLowerCase();
    if (q && !(callNumber(c).toLowerCase().includes(q))) return false;
    return true;
  });

  const total = rows.length;
  const answered = rows.filter((c) => c.status === 'answered' || c.status === 'ended').length;
  const inbound = rows.filter((c) => c.direction === 'inbound').length;
  const totalMin = Math.round(rows.reduce((a, c) => a + (c.durationSeconds || 0), 0) / 60);

  const goToCalled = (id) => history && history.push(`/${lang || 'pt'}/called-accompaniment/${id}`);

  const kpi = (icon, value, label, tint, fg) => (
    <Box sx={{ ...cardSx, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: tint, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 22, color: vt.text, lineHeight: 1.1 }}>{loading ? '—' : value}</Typography>
        <Typography sx={{ fontSize: 11.5, color: vt.textSoft }}>{label}</Typography>
      </Box>
    </Box>
  );

  const selectSx = { '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: vt.surface }, '& .MuiOutlinedInput-notchedOutline': { borderColor: vt.border } };

  return (
    <Box sx={{ ...cardSx, p: 2.5, mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: vt.greenGrad, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(85,139,47,.25)' }}>
          <PhoneInTalkIcon sx={{ fontSize: 21, color: '#fff !important' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17, color: vt.text, lineHeight: 1.15 }}>{f('voip.calls.title', 'Histórico de chamadas')}</Typography>
          <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.calls.subtitle', 'Ligações realizadas e recebidas na sua operação')}</Typography>
        </Box>
        <Button size="small" variant="outlined" disableElevation onClick={forceReconcile} disabled={reconciling}
          startIcon={reconciling ? <CircularProgress size={15} sx={{ color: vt.textSoft }} /> : <RefreshIcon sx={{ fontSize: 17 }} />}
          sx={{ textTransform: 'none', borderColor: vt.border, color: vt.textSoft, mr: 0.5, whiteSpace: 'nowrap', fontWeight: 600 }}>
          {reconciling ? f('voip.calls.reconcile.loading', 'Conciliando…') : f('voip.calls.reconcile.btn', 'Forçar conciliação')}
        </Button>
        <Tooltip title={f('voip.calls.refresh', 'Atualizar')}>
          <IconButton onClick={load} sx={{ color: vt.textSoft }}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>{kpi(<PhoneIcon />, total, f('voip.calls.kpi.total', 'Total'), '#EEF2F6', vt.textSoft)}</Grid>
        <Grid item xs={6} md={3}>{kpi(<CheckCircleOutlineIcon />, answered, f('voip.calls.kpi.answered', 'Atendidas'), vt.greenSoft, 'var(--mexx-green-dark, #558B2F)')}</Grid>
        <Grid item xs={6} md={3}>{kpi(<CallReceivedIcon />, inbound, f('voip.calls.kpi.inbound', 'Recebidas'), vt.blueSoft, vt.blue)}</Grid>
        <Grid item xs={6} md={3}>{kpi(<PhoneInTalkIcon />, `${totalMin}m`, f('voip.calls.kpi.minutes', 'Tempo total'), '#FEF3E2', '#B45309')}</Grid>
      </Grid>

      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" placeholder={f('voip.calls.search', 'Buscar por número')} value={search} onChange={(e) => setSearch(e.target.value)} sx={selectSx}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: vt.muted }} /></InputAdornment> }} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth size="small" select label={f('voip.calls.filter.direction', 'Direção')} value={direction} onChange={(e) => setDirection(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={selectSx}>
            <option value="all">{f('voip.calls.filter.all', 'Todas')}</option>
            <option value="outbound">{f('voip.calls.hist.outbound', 'Realizadas')}</option>
            <option value="inbound">{f('voip.calls.hist.inbound', 'Recebidas')}</option>
          </TextField>
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth size="small" select label={f('voip.calls.filter.status', 'Status')} value={status} onChange={(e) => setStatus(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={selectSx}>
            <option value="all">{f('voip.calls.filter.all', 'Todos')}</option>
            <option value="answered">{f('voip.call.status.answered', 'Atendida')}</option>
            <option value="initiated">{f('voip.call.status.initiated', 'Discada')}</option>
            <option value="missed">{f('voip.call.status.missed', 'Não atendida')}</option>
            <option value="failed">{f('voip.call.status.failed', 'Falhou')}</option>
          </TextField>
        </Grid>
      </Grid>

      <Box sx={{ border: `1px solid ${vt.border}`, borderRadius: 3, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11.5, color: vt.muted, letterSpacing: 0.4, bgcolor: vt.surfaceAlt, borderBottom: `1px solid ${vt.border}`, py: 1.25 } }}>
              <TableCell>{f('voip.calls.col.direction', 'DIREÇÃO')}</TableCell>
              <TableCell>{f('voip.calls.col.number', 'NÚMERO')}</TableCell>
              <TableCell>{f('voip.calls.col.when', 'DATA')}</TableCell>
              <TableCell>{f('voip.calls.col.duration', 'DURAÇÃO')}</TableCell>
              <TableCell>{f('voip.calls.col.status', 'STATUS')}</TableCell>
              <TableCell align="center">{f('voip.calls.col.recording', 'GRAVAÇÃO')}</TableCell>
              <TableCell>{f('voip.calls.col.called', 'CHAMADO')}</TableCell>
              <TableCell align="center">{f('voip.calls.col.details', 'DETALHES')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk${i}`}><TableCell colSpan={8} sx={{ borderBottom: `1px solid ${vt.border}`, py: 1.25 }}><Skeleton variant="text" height={24} /></TableCell></TableRow>
            ))}

            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} sx={{ border: 0 }}>
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: vt.surfaceAlt, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto' }}><PhoneIcon sx={{ fontSize: 30, color: vt.muted }} /></Box>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: vt.text, mt: 1.25 }}>{f('voip.calls.empty.title', 'Nenhuma chamada encontrada')}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.calls.empty.desc', 'As ligações da sua operação aparecerão aqui.')}</Typography>
                </Box>
              </TableCell></TableRow>
            )}

            {!loading && filtered.map((c) => {
              const playing = playingId === c.id;
              return (
                <React.Fragment key={c.id}>
                  <TableRow hover sx={{ '& td': { borderBottom: `1px solid ${vt.border}`, py: 1.25 } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: c.direction === 'inbound' ? vt.blueSoft : vt.greenSoft, color: c.direction === 'inbound' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }}>
                          {c.direction === 'inbound' ? <CallReceivedIcon sx={{ fontSize: 15 }} /> : <CallMadeIcon sx={{ fontSize: 15 }} />}
                        </Avatar>
                        <Typography sx={{ fontSize: 13, color: vt.text }}>{c.direction === 'inbound' ? f('voip.calls.hist.inbound', 'Recebida') : f('voip.calls.hist.outbound', 'Realizada')}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: vt.text }}>{callNumber(c)}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.25 }}>
                        <Typography sx={{ fontSize: 10.5, color: vt.muted, fontVariantNumeric: 'tabular-nums' }}>#{c.id}</Typography>
                        {c.providerName ? <Box component="span" sx={{ px: 0.6, py: '1px', borderRadius: 0.75, bgcolor: vt.surfaceAlt, border: `1px solid ${vt.border}`, fontSize: 9.5, fontWeight: 700, color: vt.textSoft, lineHeight: 1.4 }}>{c.providerName}</Box> : null}
                      </Box>
                    </TableCell>
                    <TableCell><Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{fmtWhen(c.startedAt)}</Typography></TableCell>
                    <TableCell><Typography sx={{ fontSize: 13, fontWeight: 600, color: vt.text }}>{c.durationSeconds > 0 ? fmtClock(c.durationSeconds) : '—'}</Typography></TableCell>
                    <TableCell>{statusChip(c.status)}</TableCell>
                    <TableCell align="center">
                      {c.recordingUrl ? (
                        <Tooltip title={f('voip.calls.play', 'Ouvir gravação')}>
                          <IconButton size="small" onClick={() => setPlayingId(playing ? null : c.id)} sx={{ width: 32, height: 32, bgcolor: playing ? 'var(--mexx-green-dark, #558B2F)' : vt.greenSoft, color: playing ? '#fff' : 'var(--mexx-green-dark, #558B2F)', '&:hover': { bgcolor: playing ? 'var(--mexx-green-dark, #4A7B29)' : 'var(--mexx-green-light, #E4F0D4)' } }}>
                            {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
                          </IconButton>
                        </Tooltip>
                      ) : <Typography sx={{ color: vt.muted }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      {c.calledId ? (
                        <Box onClick={() => goToCalled(c.calledId)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: 999, bgcolor: vt.blueSoft, color: vt.blue, fontWeight: 700, fontSize: 12, cursor: 'pointer', '&:hover': { bgcolor: '#E0EAFF' } }}>
                          <OpenInNewIcon sx={{ fontSize: 13 }} />#{c.calledId}
                        </Box>
                      ) : (
                        <Button size="small" startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />} onClick={() => openAssociate(c.id)}
                          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: vt.blue, '&:hover': { bgcolor: vt.blueSoft } }}>
                          {f('voip.calls.associate', 'Associar chamado')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={f('voip.calls.details', 'Mais informações')}>
                        <IconButton size="small" onClick={() => setDetailCall(c)} sx={{ color: vt.blue, '&:hover': { bgcolor: vt.blueSoft } }}>
                          <InfoOutlinedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {playing && c.recordingUrl && (
                    <TableRow><TableCell colSpan={8} sx={{ bgcolor: vt.surfaceAlt, py: 1.25, borderBottom: `1px solid ${vt.border}` }}>
                      <audio controls autoPlay preload="none" style={{ width: '100%', height: 38 }} src={c.recordingUrl} />
                    </TableCell></TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* Modal de detalhes (compartilhado) */}
      <VoipCallDetailsModal call={detailCall} onClose={() => setDetailCall(null)} />

      {/* Modal: associar chamada órfã a um chamado */}
      <Dialog open={Boolean(assocCallId)} onClose={closeAssociate} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, color: vt.text }}>{f('voip.calls.assoc.title', 'Associar a um chamado')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: vt.textSoft, mb: 2 }}>{f('voip.calls.assoc.desc', 'Informe o número do chamado para vincular esta ligação.')}</Typography>
          <TextField autoFocus fullWidth size="small" type="number" label={f('voip.calls.assoc.field', 'Número do chamado')}
            value={assocInput} disabled={associating} onChange={(e) => setAssocInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitAssociate(); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: vt.muted, fontWeight: 700 }}>#</Typography></InputAdornment> }} />
          {assocError && <Alert severity="error" sx={{ mt: 1.5 }}>{assocError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAssociate} disabled={associating} sx={{ textTransform: 'none', color: vt.textSoft, fontWeight: 600 }}>{f('voip.calls.assoc.cancel', 'Cancelar')}</Button>
          <Button onClick={submitAssociate} disabled={associating} variant="contained"
            sx={{ background: vt.greenGrad, color: '#fff', fontWeight: 700, textTransform: 'none', px: 2.5, '&:hover': { background: vt.greenGradHover }, '& path': { fill: '#fff !important' } }}>
            {associating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : f('voip.calls.assoc.confirm', 'Associar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnack('')} sx={{ '& path': { fill: '#fff !important' } }}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}

export default injectIntl(VoipCallsHistory);
